import React, { useState } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { LineChart, BarChart3, TrendingUp } from 'lucide-react';

const PerformanceTrendChart = ({ 
  data,
  trendData,
  chartType, 
  period, 
  metric,
  onChartTypeChange, 
  onPeriodChange,
  onMetricChange 
}) => {
  const [showMovingAverage, setShowMovingAverage] = useState(true);

  // Use trendData if available, otherwise fall back to data
  const chartData = trendData?.data || data || [];

  if (!chartData || !chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 bg-muted/50 rounded-lg">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartDataKey = metric === 'ctr' ? 'ctr' : 
                       metric === 'fill_rate' ? 'fill_rate' : 
                       metric === 'completion_rate' ? 'completion_rate' : 
                       metric === 'active_users' ? 'active_users' :
                       metric === 'sessions' ? 'sessions' :
                       metric === 'clicks' ? 'clicks' : 'impressions';

  const formatChartValue = (value) => {
    if (metric === 'ctr' || metric === 'fill_rate' || metric === 'completion_rate') {
      return `${value?.toFixed(2) || 0}%`;
    }
    return value?.toLocaleString() || '0';
  };

  const formatYAxisTick = (value) => {
    if (metric === 'ctr' || metric === 'fill_rate' || metric === 'completion_rate') {
      return `${value}%`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  const metricLabel = metric.charAt(0).toUpperCase() + metric.slice(1).replace('_', ' ');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Performance Trend</CardTitle>
          <div className="flex items-center gap-3">
            {trendData && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-moving-average-perf"
                  checked={showMovingAverage}
                  onCheckedChange={setShowMovingAverage}
                />
                <label
                  htmlFor="show-moving-average-perf"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Moving Average
                </label>
              </div>
            )}
            <Select value={period} onValueChange={onPeriodChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            <Select value={metric} onValueChange={onMetricChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="impressions">Impressions</SelectItem>
                <SelectItem value="clicks">Clicks</SelectItem>
                <SelectItem value="ctr">CTR</SelectItem>
                <SelectItem value="fill_rate">Fill Rate</SelectItem>
                <SelectItem value="completion_rate">Completion Rate</SelectItem>
                <SelectItem value="active_users">Active Users</SelectItem>
                <SelectItem value="sessions">Sessions</SelectItem>
              </SelectContent>
            </Select>
            <Select value={chartType} onValueChange={onChartTypeChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">
                  <div className="flex items-center gap-2">
                    <LineChart className="w-4 h-4" />
                    <span>Line</span>
                  </div>
                </SelectItem>
                <SelectItem value="bar">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    <span>Bar</span>
                  </div>
                </SelectItem>
                <SelectItem value="area">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Area</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280" 
                tick={{ fill: '#6b7280', fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={formatYAxisTick} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                formatter={(value, name) => {
                  if (name === chartDataKey) {
                    const formatted = formatChartValue(value);
                    return formatted;
                  }
                  if (name === 'moving_average') {
                    return `${formatChartValue(value)} (MA)`;
                  }
                  return formatChartValue(value);
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} iconSize={10} />
              <Bar dataKey={chartDataKey} fill="#3b82f6" name={metricLabel} />
            </BarChart>
          ) : chartType === 'area' ? (
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={formatYAxisTick} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name) => {
                  if (name === chartDataKey) {
                    return formatChartValue(value);
                  }
                  if (name === 'moving_average') {
                    return `${formatChartValue(value)} (MA)`;
                  }
                  return formatChartValue(value);
                }}
              />
              <Legend />
              <Area type="monotone" dataKey={chartDataKey} stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name={metricLabel} />
              {showMovingAverage && trendData && (
                <Line type="monotone" dataKey="moving_average" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Moving Average" dot={false} />
              )}
            </AreaChart>
          ) : (
            <RechartsLineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={formatYAxisTick} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name, props) => {
                  if (name === chartDataKey) {
                    const formatted = formatChartValue(value);
                    const row = props.payload;
                    if (row.growth_rate_percent !== null && row.growth_rate_percent !== undefined) {
                      return `${formatted} (${row.growth_rate_percent > 0 ? '+' : ''}${row.growth_rate_percent.toFixed(2)}%)`;
                    }
                    return formatted;
                  }
                  if (name === 'moving_average') {
                    return `${formatChartValue(value)} (MA)`;
                  }
                  return formatChartValue(value);
                }}
              />
              <Legend />
              <Line type="monotone" dataKey={chartDataKey} stroke="#3b82f6" strokeWidth={2} name={metricLabel} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              {showMovingAverage && trendData && (
                <Line type="monotone" dataKey="moving_average" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Moving Average" dot={false} />
              )}
            </RechartsLineChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default PerformanceTrendChart;
